import React, { useState } from 'react';
import { useAdminAuth } from '../../contexts/AdminAuthContext';
import { api } from '../../lib/api';
import Input from '../ui/Input';
import Button from '../ui/Button';
import Icon from '../AppIcon';

const AudioUploadField = ({
  label = "Audio File",
  value,
  onChange,
  placeholder = 'Enter audio URL or upload',
  description,
  error,
}) => {
  const { token } = useAdminAuth();
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState(null);

  const handleFileSelect = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file type
    const validTypes = ['audio/mpeg', 'audio/mp3', 'audio/wav', 'audio/ogg', 'audio/webm'];
    if (!validTypes.includes(file.type)) {
      setUploadError('Invalid file type. Please upload MP3, WAV, or OGG');
      return;
    }

    // Validate file size (20MB max)
    const maxSize = 20 * 1024 * 1024;
    if (file.size > maxSize) {
      setUploadError('File too large. Maximum size: 20MB');
      return;
    }

    setUploading(true);
    setUploadError(null);

    try {
      const result = await api.uploadAudio(file, token);
      if (result?.url) {
        onChange({ target: { value: result.url } });
      }
    } catch (err) {
      console.error('Audio upload error:', err);
      setUploadError(err.message || 'Failed to upload audio');
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="space-y-2">
      <div className="flex gap-2 items-end">
        <div className="flex-1">
          <Input
            label={label}
            type="text"
            value={value || ''}
            onChange={onChange}
            placeholder={placeholder}
            description={description}
            error={error || uploadError}
          />
        </div>
        <div className="relative">
          <input
            type="file"
            accept="audio/*"
            onChange={handleFileSelect}
            className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
            disabled={uploading}
          />
          <Button
            variant="outline"
            size="default"
            disabled={uploading}
            type="button"
          >
            {uploading ? (
              <Icon name="Loader2" size={16} className="animate-spin" />
            ) : (
              <Icon name="Upload" size={16} />
            )}
          </Button>
        </div>
      </div>

      {value && (
        <div className="p-3 bg-muted/30 rounded-lg">
          <audio controls className="w-full" src={value}>
            Your browser does not support the audio element.
          </audio>
        </div>
      )}
    </div>
  );
};

export default AudioUploadField;
